import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

export function formatTechnologyDate(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export function getTimestampMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export function normalizeLikesFields(row) {
  return {
    ...row,
    likes: row.likes ?? 0,
    likedBy: row.likedBy ?? [],
  };
}

export async function toggleResourceLike({ collectionName, docId }) {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('You must be logged in to like resources.');
  }

  const ref = doc(db, collectionName, docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error('Resource not found.');
  }

  const data = snap.data();
  const userId = auth.currentUser.uid;
  const likedBy = data.likedBy || [];
  const isLiked = likedBy.includes(userId);
  const currentLikes = data.likes ?? likedBy.length ?? 0;

  if (isLiked) {
    await updateDoc(ref, {
      likes: Math.max(0, currentLikes - 1),
      likedBy: arrayRemove(userId),
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(ref, {
      likes: currentLikes + 1,
      likedBy: arrayUnion(userId),
      updatedAt: serverTimestamp(),
    });
  }

  return { docId, isLiked: !isLiked, likes: isLiked ? Math.max(0, currentLikes - 1) : currentLikes + 1 };
}

export function filterResourcesBySearch(items, searchTerm, fields = ['title', 'description', 'authorName', 'fileName', 'name']) {
  const q = searchTerm?.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    fields.some((field) => {
      const val = item[field];
      return val && String(val).toLowerCase().includes(q);
    })
  );
}

export function sortResources(items, sortBy) {
  const list = [...items];
  if (sortBy === 'likes') {
    list.sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0));
  } else if (sortBy === 'alpha') {
    list.sort((a, b) => {
      const ta = (a.title || a.name || a.fileName || '').toLowerCase();
      const tb = (b.title || b.name || b.fileName || '').toLowerCase();
      return ta.localeCompare(tb);
    });
  } else {
    list.sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt));
  }
  return list;
}

export async function downloadRemoteFile(url, filename) {
  const safeName = filename?.trim() || 'download';
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = safeName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

export const JOBTREAD_IMPORT_TYPES = [
  { id: 'cost-groups', label: 'Cost Groups', emoji: '💰' },
  { id: 'schedules', label: 'Schedules', emoji: '📅' },
  { id: 'dashboards', label: 'Dashboards', emoji: '📊' },
  { id: 'todos', label: "To-Dos", emoji: '✅' },
  { id: 'workflows', label: 'Workflows', emoji: '🔁' },
];

export const RESOURCE_TYPE_LABELS = {
  automation: 'Automation',
  'jobtread-import': 'JobTread Import',
  'claude-skill': 'Skill',
  app: 'App',
};
